import React, { lazy, LazyExoticComponent, ReactElement } from 'react'
import { AuthoringUtils, Model } from '@adobe/aem-spa-page-model-manager'

type AEMComponentMapType = {
  [key: string]: LazyExoticComponent<() => React.JSX.Element>
}

// This is for when in AEM authoring environments
export const whichAEMComponentToServe = (): {
  element: ReactElement | null
} => {
  const authoringInAEM = AuthoringUtils.isInEditor()
  if (!authoringInAEM) return { element: null }

  const component = getAEMComponentName()

  const AEMComponentMap: AEMComponentMapType = {
    '/customer-profile': lazy(() => import('@aem/AppAEM')),
    '/customer-profile/account-settings': lazy(() => import('@aem/AccountSettingsTabAEM')),
    '/customer-profile/terms-and-conditions': lazy(() => import('@aem/TermsAndConditionsTabAEM')),
    '/customer-profile/terms-and-conditions/fin-simple': lazy(() => import('@aem/termsAndConditions/FinSimpleAEM')),
    '/customer-profile/terms-and-conditions/one-time-payment': lazy(
      () => import('@aem/termsAndConditions/OneTimePaymentAEM')
    ),
    '/customer-profile/terms-and-conditions/automatic-payment': lazy(
      () => import('@aem/termsAndConditions/AutomaticPaymentAEM')
    ),
    '/customer-profile/account-settings/contact-information': lazy(
      () => import('@aem/contactInformation/ContactInformationAEM')
    ),
    '/customer-profile/account-settings/contact-information/edit-email': lazy(
      () => import('@aem/contactInformation/EditEmailModalAEM')
    ),
    '/customer-profile/account-settings/contact-information/add-phone-number': lazy(
      () => import('@aem/contactInformation/AddPhoneNumberModalAEM')
    ),
    '/customer-profile/account-settings/contact-information/confirm-delete': lazy(
      () => import('@aem/contactInformation/ConfirmDeleteModalAEM')
    ),
    '/customer-profile/account-settings/contact-information/edit-billing-address': lazy(
      () => import('@aem/contactInformation/EditBillingAddressModalAEM')
    ),
    '/customer-profile/account-settings/contact-information/edit-garaging-address': lazy(
      () => import('@aem/contactInformation/EditGaragingAddressModalAEM')
    ),
    '/customer-profile/account-settings/contact-information/edit-phone-number': lazy(
      () => import('@aem/contactInformation/EditPhoneNumberModalAEM')
    ),
    '/customer-profile/account-settings/notification-preferences': lazy(
      () => import('@aem/notificationPreferences/NotificationPreferencesTabAEM')
    ),
    '/customer-profile/account-settings/notification-preferences/go-paperless': lazy(
      () => import('@aem/notificationPreferences/GoPaperlessAEM')
    ),
    '/customer-profile/account-settings/notification-preferences/learn-more': lazy(
      () => import('@aem/notificationPreferences/LearnMoreModalAEM')
    ),
    '/customer-profile/account-settings/notification-preferences/online-statement': lazy(
      () => import('@aem/notificationPreferences/OnlineStatementAEM')
    ),
    '/customer-profile/account-settings/notification-preferences/payment-received': lazy(
      () => import('@aem/notificationPreferences/PaymentReceivedAEM')
    ),
    '/customer-profile/account-settings/notification-preferences/special-offers': lazy(
      () => import('@aem/notificationPreferences/SpecialOffersAEM')
    ),
    '/customer-profile/account-settings/notification-preferences/upcoming-payment': lazy(
      () => import('@aem/notificationPreferences/UpcomingPaymentAEM')
    ),
  }

  if (!component || !(component in AEMComponentMap))
    return {
      element: (
        <div style={{ fontSize: '24px' }}>
          <p>Looks like you're in AEM, but I don't know which component to show you. 🤷</p>
          <p>Ensure the page you are expecting is part of the AEMComponentMap in aem.tsx</p>
        </div>
      ),
    }

  const AEMViewToServe = AEMComponentMap[component]
  return { element: <AEMViewToServe /> }
}

function getAEMComponentName(): string | undefined {
  const host = window.location.href.replace(/\.html$/, '')
  const startIndex = host.indexOf('/customer-profile')
  if (startIndex === -1) {
    return undefined
  }
  return host.substring(startIndex)
}

export enum AEMPagePath {
  CUSTOMER_PROFILE = '/content/ucl/finsimple/ford/en-us/customer-profile',
  ACCOUNT_SETTINGS = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings',
  TERMS_AND_CONDITIONS = '/content/ucl/finsimple/ford/en-us/customer-profile/terms-and-conditions',
  TERMS_AND_CONDITIONS_FIN_SIMPLE = '/content/ucl/finsimple/ford/en-us/customer-profile/terms-and-conditions/fin-simple',
  TERMS_AND_CONDITIONS_ONE_TIME_PAYMENT = '/content/ucl/finsimple/ford/en-us/customer-profile/terms-and-conditions/one-time-payment',
  TERMS_AND_CONDITIONS_AUTOMATIC_PAYMENT = '/content/ucl/finsimple/ford/en-us/customer-profile/terms-and-conditions/automatic-payment',
  CONTACT_INFORMATION = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/contact-information',
  EMAIL_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/contact-information/edit-email',
  ADD_PHONE_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/contact-information/add-phone-number',
  CONFIRM_DELETE_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/contact-information/confirm-delete',
  EDIT_BILLING_ADDRESS_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/contact-information/edit-billing-address',
  EDIT_GARAGING_ADDRESS_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/contact-information/edit-garaging-address',
  EDIT_PHONE_NUMBER_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/contact-information/edit-phone-number',
  NOTIFICATION_PREFERENCES = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/notification-preferences',
  GO_PAPER_LESS_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/notification-preferences/go-paperless',
  LEARN_MORE_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/notification-preferences/learn-more',
  ONLINE_STATEMENT_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/notification-preferences/online-statement',
  PAYMENT_RECEIVED_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/notification-preferences/payment-received',
  SPECIAL_OFFERS_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/notification-preferences/special-offers',
  UP_COMING_PAYMENT_MODAL = '/content/ucl/finsimple/ford/en-us/customer-profile/account-settings/notification-preferences/upcoming-payment',
}

export const getAEMHost = () =>
  AuthoringUtils.isInEditor() ? process.env.AEM_AUTHOR_URL : process.env.AEM_PUBLISHER_URL

export const parseModelJSON = (modelData: Model) => {
  const modelParsed = modelData?.[':items']?.['root']?.[':items']?.['responsivegrid']?.[':items']
  return modelParsed
}

export const getAEMValue = (
  key: string,
  parsedModel: Model,
  keyType: string,
  removeTags: boolean = false,
  removeKeywords: Array<string> = []
) => {
  if (!parsedModel) {
    return ''
  }
  const tags = ['<h1>', '</h1>', '<h2>', '</h2>', '<h3>', '</h3>', '<h4>', '</h4>', '<h5>', '</h5>', '<h6>', '</h6>']
  if (!(parsedModel as any)[key]) {
    return ''
  }
  let value = (parsedModel as any)[key][keyType]
  removeKeywords.forEach((keyword) => {
    value = value.replace(keyword, '')
  })
  if (removeTags) {
    tags.forEach((t) => {
      value = value.replace(t, '')
    })
  }
  return value
}
